import { useState, useEffect, useMemo } from 'react'
import { classes, reports, feedback } from '@/api/client'
import type { ClassInfo, FeedbackEntry } from '@/api/client'
import { useSort, sortRows, SortTh, type SortDir, type SortValue } from './sortable'

/**
 * EngagementReport — weekly-active, per-boss completion/drop-off, and student
 * feedback, from the events + feedback tables. The weekly check-in view for
 * the beta (see docs/product/METRICS_AND_INSTRUMENTATION.md).
 */

type Engagement = Awaited<ReturnType<typeof reports.engagement>>
type FeedbackData = Awaited<ReturnType<typeof feedback.list>>

const px = { fontFamily: '"Press Start 2P", monospace' }

type BossRow = Engagement['bosses'][number]
type BossKey = 'boss' | 'students' | 'starts' | 'completes' | 'abandons' | 'completion' | 'time'
const BOSS_COLUMNS: { key: BossKey; label: string; first: SortDir; get: (b: BossRow) => SortValue }[] = [
  { key: 'boss',       label: 'Boss',       first: 'asc',  get: b => b.boss_name ?? b.standard_id },
  { key: 'students',   label: 'Students',   first: 'desc', get: b => b.students },
  { key: 'starts',     label: 'Starts',     first: 'desc', get: b => b.starts },
  { key: 'completes',  label: 'Completes',  first: 'desc', get: b => b.completes },
  { key: 'abandons',   label: 'Abandons',   first: 'desc', get: b => b.abandons },
  { key: 'completion', label: 'Completion', first: 'desc', get: b => b.completion_rate },
  { key: 'time',       label: 'Avg time',   first: 'desc', get: b => b.avg_duration_s },
]

type FbRow = FeedbackData['boss_summary'][number]
type FbKey = 'boss' | 'too_easy' | 'just_right' | 'too_hard' | 'confused'
const FB_COLUMNS: { key: FbKey; label: string; first: SortDir; get: (r: FbRow) => SortValue }[] = [
  { key: 'boss',       label: 'Boss',          first: 'asc',  get: r => r.boss_name ?? r.standard_id },
  { key: 'too_easy',   label: '😴 Too easy',   first: 'desc', get: r => r.too_easy },
  { key: 'just_right', label: '😎 Just right', first: 'desc', get: r => r.just_right },
  { key: 'too_hard',   label: '🔥 Too hard',   first: 'desc', get: r => r.too_hard },
  { key: 'confused',   label: '😵 Confused',   first: 'desc', get: r => r.confused },
]

function fmtDuration(s: number | null): string {
  if (s === null) return '—'
  if (s < 60) return `${s}s`
  return `${Math.round(s / 60)}m ${s % 60}s`
}

function fmtWhen(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function EngagementReport() {
  const [classList, setClassList] = useState<(ClassInfo & { class_code: string; student_count: number })[]>([])
  const [classId, setClassId]     = useState<number | null>(null)
  const [data, setData]           = useState<Engagement | null>(null)
  const [fb, setFb]               = useState<FeedbackData | null>(null)
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')

  // Both tables keep server (curriculum) order until a header is clicked.
  const bossSort = useSort<BossKey>(null)
  const fbSort   = useSort<FbKey>(null)
  const bossRows = useMemo(() => {
    const col = BOSS_COLUMNS.find(c => c.key === bossSort.key)
    return sortRows(data?.bosses ?? [], col ? col.get : null, bossSort.dir, b => b.boss_name ?? b.standard_id)
  }, [data, bossSort.key, bossSort.dir])
  const fbRows = useMemo(() => {
    const col = FB_COLUMNS.find(c => c.key === fbSort.key)
    return sortRows(fb?.boss_summary ?? [], col ? col.get : null, fbSort.dir, r => r.boss_name ?? r.standard_id)
  }, [fb, fbSort.key, fbSort.dir])

  useEffect(() => {
    classes.list()
      .then(list => {
        setClassList(list as any)
        if (list.length > 0) setClassId(list[0].id)
      })
      .catch(err => setError(err.message || 'Failed to load classes'))
  }, [])

  useEffect(() => {
    if (!classId) return
    setLoading(true)
    setError('')
    Promise.all([reports.engagement(classId), feedback.list(classId)])
      .then(([eng, fbData]) => { setData(eng); setFb(fbData) })
      .catch(err => setError(err.message || 'Failed to load engagement data'))
      .finally(() => setLoading(false))
  }, [classId])

  const statBox = (label: string, value: string | number, hint?: string) => (
    <div style={{ background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 8,
                  padding: '14px 18px', minWidth: 150 }}>
      <div style={{ ...px, fontSize: 16, color: '#a78bfa' }}>{value}</div>
      <div style={{ ...px, fontSize: 7, color: '#6b7280', marginTop: 8 }}>{label}</div>
      {hint && <div style={{ fontFamily: 'system-ui', fontSize: 11, color: '#4b5563', marginTop: 4 }}>{hint}</div>}
    </div>
  )

  const textFeedback = (fb?.feedback ?? []).filter((f: FeedbackEntry) => f.text)

  return (
    <div>
      {/* Class picker */}
      <div className="flex items-center gap-3 mb-5">
        <span style={{ ...px, fontSize: 8, color: '#6b7280' }}>CLASS</span>
        <select value={classId ?? ''} onChange={e => setClassId(Number(e.target.value))}
          style={{ background: '#0f0f23', border: '1px solid #312e81', color: '#e0e7ff',
                   padding: '6px 10px', borderRadius: 6, fontFamily: 'system-ui', fontSize: 13 }}>
          {classList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {loading && <span style={{ ...px, fontSize: 8, color: '#6366f1' }}>LOADING…</span>}
      </div>

      {error && <div style={{ color: '#f87171', fontFamily: 'system-ui', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {data && (
        <>
          {/* Headline stats */}
          <div className="flex flex-wrap gap-3 mb-6">
            {statBox('ACTIVE THIS WEEK', `${data.weekly_active_students}/${data.roster_count}`,
              'students who played in the last 7 days')}
            {statBox('BOSSES STARTED', data.bosses.reduce((n, b) => n + b.starts, 0))}
            {statBox('BOSSES DEFEATED', data.bosses.reduce((n, b) => n + b.completes, 0))}
            {statBox('ABANDONED', data.bosses.reduce((n, b) => n + b.abandons, 0),
              'started but quit mid-battle')}
          </div>

          {/* Weekly series */}
          {data.weekly_series.length > 0 && (
            <div style={{ background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 8,
                          padding: 16, marginBottom: 24 }}>
              <div style={{ ...px, fontSize: 8, color: '#6b7280', marginBottom: 10 }}>ACTIVE STUDENTS BY WEEK</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
                {data.weekly_series.map(w => (
                  <div key={w.week} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ ...px, fontSize: 7, color: '#a78bfa' }}>{w.active_students}</span>
                    <div style={{
                      width: 28,
                      height: Math.max(4, (w.active_students / Math.max(1, data.roster_count)) * 56),
                      background: '#6366f1', borderRadius: 3,
                    }}/>
                    <span style={{ fontFamily: 'system-ui', fontSize: 10, color: '#4b5563' }}>wk {w.week.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-boss table */}
          <div style={{ background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 8,
                        padding: 16, marginBottom: 24, overflowX: 'auto' }}>
            <div style={{ ...px, fontSize: 8, color: '#6b7280', marginBottom: 10 }}>
              PER-BOSS FUNNEL <span style={{ color: '#4b5563' }}>· high abandons = where kids stall</span>
            </div>
            {data.bosses.length === 0 ? (
              <div style={{ fontFamily: 'system-ui', fontSize: 13, color: '#4b5563' }}>
                No gameplay events yet — data appears once students start playing.
              </div>
            ) : (
              <table style={{ width: '100%', fontFamily: 'system-ui', fontSize: 13, color: '#cbd5e1', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                    {BOSS_COLUMNS.map(c => (
                      <SortTh key={c.key} label={c.label} style={{ padding: '4px 8px' }}
                        active={bossSort.key === c.key} dir={bossSort.dir}
                        onClick={() => bossSort.toggle(c.key, c.first)} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bossRows.map(b => (
                    <tr key={b.standard_id} style={{ borderTop: '1px solid #1e1b4b' }}>
                      <td style={{ padding: '6px 8px' }}>
                        {b.boss_name ?? b.standard_id}
                        <span style={{ color: '#4b5563', marginLeft: 6, fontSize: 11 }}>{b.standard_id}</span>
                      </td>
                      <td style={{ padding: '6px 8px' }}>{b.students}</td>
                      <td style={{ padding: '6px 8px' }}>{b.starts}</td>
                      <td style={{ padding: '6px 8px' }}>{b.completes}</td>
                      <td style={{ padding: '6px 8px', color: b.abandons > b.completes ? '#f87171' : undefined }}>
                        {b.abandons}
                      </td>
                      <td style={{ padding: '6px 8px' }}>{b.completion_rate === null ? '—' : `${b.completion_rate}%`}</td>
                      <td style={{ padding: '6px 8px' }}>{fmtDuration(b.avg_duration_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Student feedback */}
      {fb && (
        <div style={{ background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 8, padding: 16 }}>
          <div style={{ ...px, fontSize: 8, color: '#6b7280', marginBottom: 10 }}>STUDENT FEEDBACK</div>

          {fb.boss_summary.length > 0 && (
            <table style={{ fontFamily: 'system-ui', fontSize: 13, color: '#cbd5e1',
                            borderCollapse: 'collapse', marginBottom: 14 }}>
              <thead>
                <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                  {FB_COLUMNS.map(c => (
                    <SortTh key={c.key} label={c.label} style={{ padding: '4px 8px' }}
                      active={fbSort.key === c.key} dir={fbSort.dir}
                      onClick={() => fbSort.toggle(c.key, c.first)} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {fbRows.map(s => (
                  <tr key={s.standard_id} style={{ borderTop: '1px solid #1e1b4b' }}>
                    <td style={{ padding: '6px 8px' }}>{s.boss_name ?? s.standard_id}</td>
                    <td style={{ padding: '6px 8px' }}>{s.too_easy}</td>
                    <td style={{ padding: '6px 8px' }}>{s.just_right}</td>
                    <td style={{ padding: '6px 8px', color: s.too_hard > s.just_right ? '#f59e0b' : undefined }}>{s.too_hard}</td>
                    <td style={{ padding: '6px 8px', color: s.confused > 0 ? '#f87171' : undefined }}>{s.confused}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {textFeedback.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {textFeedback.slice(0, 30).map((f: FeedbackEntry) => (
                <div key={f.id} style={{ fontFamily: 'system-ui', fontSize: 13, color: '#cbd5e1' }}>
                  <span style={{ color: '#6b7280' }}>{fmtWhen(f.created_at)} · {f.student_name ?? 'teacher'}: </span>
                  “{f.text}”
                </div>
              ))}
            </div>
          ) : fb.boss_summary.length === 0 && (
            <div style={{ fontFamily: 'system-ui', fontSize: 13, color: '#4b5563' }}>
              No feedback yet — students see a one-tap prompt after each boss.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
